/**
 * Хуанли — өдөр бүрийн борлуулалт, гутлын урсгал (веб `/calendar`).
 *
 * Сервер мөрүүдийг уншаад ЭНД Улаанбаатарын өдрөөр хуваана: өдрийн
 * зааг, мөнгөний нийлбэр нэг газар, тест нь DB-гүй.
 *
 * ⚠️ Мөнгө = POS-ын өдрийн хаалт (`summarizeDay`). Хуанлийн нүд,
 *    өдрийн дэлгэрэнгүй, салбарын кассын цаас гурвуулаа НЭГ функцээр
 *    бодогдоно — нэг өдөрт гурван өөр тоо гарахгүй.
 * ⚠️ «Орлого» = тэр өдрийн хүчинтэй баримтуудын цэвэр борлуулалт
 *    (хөнгөлөлт хассан, Зээл/И-баримтын нэмэгдэлгүй). Хожим буцаагдсан
 *    гутал тэр өдрийн орлогоос ХАСАГДАХГҮЙ — буцаалт нь буцаасан
 *    өдөртөө тусдаа харагдана. `/reports/sales` буцаагдсан мөрийг
 *    хасдаг тул буцаалттай өдөр хоёр тоо зөрж болно: хуанли «тэр өдөр
 *    юу болсон бэ» гэдгийг хадгална, дараа нь өөрчлөгдөхгүй.
 * ⚠️ «Агуулахад орж ирсэн» = агуулахын (`stores.type = WAREHOUSE`)
 *    байршилд RECEIVE хөдөлгөөн, ШИЛЖҮҮЛГЭЭС БУСАД — салбараас буцаж
 *    ирсэн гутал шинэ бараа биш, «Шилжүүлэг» хэсэгт харагдана. Эхний
 *    үлдэгдэл (`ADJUST`) энд орохгүй, «Шинээр бүртгэгдсэн»-д орно.
 */
import { Decimal } from 'decimal.js';
import { summarizeDay } from './day-close.js';
import { fromUlaanbaatar, periodBounds, ulaanbaatarDateKey } from './revenue.js';
import { ulaanbaatarDateStart } from './report-range.js';
const MONTH_RE = /^(\d{4})-(\d{2})$/;
/** `"2026-09"` → УБ-ийн сарын хил `[from, to)`. Буруу бол null. */
export function calendarMonthRange(month) {
    const m = MONTH_RE.exec(month);
    if (!m || Number(m[2]) < 1 || Number(m[2]) > 12)
        return null;
    const { from, to } = periodBounds('month', month);
    return { from, to };
}
/** `"2026-09-28"` → УБ-ийн өдрийн хил `[from, to)`. Буруу бол null. */
export function calendarDayRange(date) {
    const from = ulaanbaatarDateStart(date);
    if (!from)
        return null;
    const [y, m, d] = date.split('-').map(Number);
    // Date.UTC нь 31+1-ийг дараагийн сар руу эргүүлнэ — offset-ыг Intl-ээс дахин уншина
    return { from, to: fromUlaanbaatar(y, m, d + 1) };
}
/** `"2026-01"`, -1 → `"2025-12"` */
export function shiftMonth(month, delta) {
    const [y, m] = month.split('-').map(Number);
    const idx = y * 12 + (m - 1) + delta;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}
/** `"2026-09-30"`, 1 → `"2026-10-01"` — хуанлийн өдөр, цагийн бүсгүй. */
export function shiftDate(date, delta) {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}
/** Сарын бүх өдрийн түлхүүр: `"2026-09-01"` … `"2026-09-30"`. */
export function monthDateKeys(month) {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return Array.from({ length: last }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`);
}
function groupBy(rows, key) {
    const out = new Map();
    for (const r of rows) {
        const k = key(r);
        const bucket = out.get(k) ?? [];
        bucket.push(r);
        out.set(k, bucket);
    }
    return out;
}
const groupByDay = (rows, at) => groupBy(rows, (r) => ulaanbaatarDateKey(at(r)));
/**
 * Сарын нүднүүд. Сарын гадуурх мөр (хил дээрх) тоологдохгүй.
 *
 * `warehouseIn` — нэг гутал нэг өдөр хоёр удаа хүлээн авагдвал НЭГ.
 */
export function calendarMonth(month, input) {
    const orders = groupByDay(input.orders, (o) => o.soldAt);
    const returns = groupByDay(input.returns, (r) => r.returnedAt);
    const registered = groupByDay(input.registered, (d) => d);
    const arrived = groupByDay(input.warehouseIn, (m) => m.movedAt);
    const days = monthDateKeys(month).map((date) => {
        const s = summarizeDay(orders.get(date) ?? [], []);
        return {
            date,
            orders: s.orders,
            units: s.units,
            salesMnt: s.salesMnt,
            returns: returns.get(date)?.length ?? 0,
            registered: registered.get(date)?.length ?? 0,
            warehouseIn: new Set((arrived.get(date) ?? []).map((m) => m.itemId)).size,
        };
    });
    const add = (k) => days.reduce((a, d) => a + d[k], 0);
    return {
        month,
        days,
        totals: {
            orders: add('orders'),
            units: add('units'),
            salesMnt: days.reduce((a, d) => a.plus(d.salesMnt), new Decimal(0)).toFixed(2),
            returns: add('returns'),
            registered: add('registered'),
            warehouseIn: add('warehouseIn'),
        },
    };
}
/**
 * Салбар тус бүрийн борлуулалт — кассын цаастай ижил тоо
 * (`summarizeDay`). Зөвхөн хүчингүй баримттай салбар гарахгүй.
 */
export function salesByStore(orders) {
    return [...groupBy(orders, (o) => o.storeId).entries()]
        .map(([storeId, list]) => {
        const s = summarizeDay(list, []);
        return { storeId, name: list[0].storeName, orders: s.orders, units: s.units, salesMnt: s.salesMnt };
    })
        .filter((s) => s.orders > 0)
        .sort((a, b) => new Decimal(b.salesMnt).comparedTo(a.salesMnt) || a.name.localeCompare(b.name));
}
/**
 * Шинэ гутал аялал тус бүрээр — бүртгэгдсэн ба агуулахад орсон хоёр
 * тоо НЭГ мөрөнд. Агуулахын тоо гутлаар давхардалгүй.
 */
export function intakeByTrip(registered, warehouseIn) {
    const rows = new Map();
    const row = (t) => {
        const cur = rows.get(t.id) ?? { ...t, registered: 0, warehouseIn: 0 };
        rows.set(t.id, cur);
        return cur;
    };
    for (const r of registered)
        row(r.trip).registered += 1;
    const seen = new Set();
    for (const m of warehouseIn) {
        if (seen.has(m.itemId))
            continue;
        seen.add(m.itemId);
        row(m.trip).warehouseIn += 1;
    }
    return [...rows.values()].sort((a, b) => b.registered + b.warehouseIn - (a.registered + a.warehouseIn) ||
        (a.code ?? '').localeCompare(b.code ?? ''));
}
//# sourceMappingURL=calendar.js.map